// SAM-624 — the service data the booking flow prices against.
//
// The service LIST (`GET /service`, useServices) returns the raw admin row: every rate group with
// its eligibility, the full price matrix, tier_groups and roles. The booking flow must NOT price
// against that. `GET /services/description/{id}` (useServiceById) is what the guest app reads: it
// already strips the rate groups (and their price cells) the viewer cannot buy, adds a top-level
// `rate_groups`, and exposes only the public part of `pricing` (price_unit, unit, seasons).
//
// `mergeServiceDetail` overlays that response on the list row, keeping the list-only fields the
// booking flow still needs (`booking_engine_service_id`, `duration`, `name`, `tier_groups`).
// Pure, no React — see bookingService.test.ts.

import type { Service, ServicePricing } from '../../../hooks/useServices';

/** A rate group as the description endpoint ships it: no eligibility, no price cells. */
export interface BookingRateGroup {
  id: string;
  label?: { en?: string | null; es?: string | null } | null;
  is_default?: boolean;
  max_qty_per_booking?: number | null;
  max_qty_per_day?: number | null;
}

export type BookingService = Omit<Service, 'pricing'> & {
  pricing?: Omit<ServicePricing, 'rate_groups'> | null;
  /** Groups the viewer may buy, from the description endpoint. Empty/absent = no rate groups. */
  rate_groups?: BookingRateGroup[];
};

export type ServiceDetail = Partial<Omit<Service, 'pricing'>> & {
  pricing?: Omit<ServicePricing, 'rate_groups'> | null;
  rate_groups?: BookingRateGroup[];
};

export function mergeServiceDetail(base: Service, detail: ServiceDetail): BookingService {
  const { rate_groups: _ignoredAdminGroups, ...basePricing } = (base.pricing || {}) as ServicePricing;
  const merged: BookingService = {
    ...base,
    ...detail,
    id: base.id,
    // Fields the description endpoint does not return must survive from the list row.
    name: base.name,
    duration: base.duration,
    booking_engine_service_id: base.booking_engine_service_id,
    pricing: { ...basePricing, ...(detail.pricing || {}) },
    rate_groups: Array.isArray(detail.rate_groups) ? detail.rate_groups : [],
  };
  return merged;
}

/** The backend's `{en, es}` label in `lang` (SAM-683), else the other language, else `fallback`. */
export function labelOf(
  label: { en?: string | null; es?: string | null } | null | undefined,
  fallback: string,
  lang: 'en' | 'es' = 'en',
): string {
  return (lang === 'es' ? label?.es || label?.en : label?.en || label?.es) || fallback;
}
